import _findKey from 'lodash/findKey';
import * as XLSX from 'xlsx';
import _get from 'lodash/get';
import _find from 'lodash/find';
import _isEmpty from 'lodash/isEmpty';
import langUtils from '../../../../utils/langUtils';
import JSZip from 'jszip';
import {saveAs} from 'file-saver';
import {dataURLtoFile, uploadFile} from '../../../../utils/skincareUtils';
import skincareProDao from '../../../../dao/skincareProDao';
import _cloneDeep from 'lodash/cloneDeep';

const productBasicInfoForAdd = [
  {
    column: 'A',
    text: 'Category*',
    comments: [{a: 'Perfect Corp.', t: 'Required. The type of your product.'}],
    isRequired: true,
    key: 'category',
  },
  {
    column: 'B',
    text: 'Action URL',
    comments: [{a: 'Perfect Corp.', t: 'Optional. The link to your product.'}],
    isRequired: false,
    key: 'actionUrl',
  },
  {
    column: 'C',
    text: 'Default Language*',
    comments: [
      {
        a: 'Perfect Corp.',
        t:
          'Required. The default language will be displayed when the product name and description have not been translated into the language selected by the customer.',
      },
    ],
    isRequired: true,
    key: 'defaultLanguage',
  },
  {
    column: 'D',
    text: 'Product Name*',
    comments: [
      {
        a: 'Perfect Corp.',
        t:
          'Required. The name of your product. The maximum character limit is 200.',
      },
    ],
    isRequired: true,
    key: 'productName',
  },

  {
    column: 'E',
    text: 'Product Description',
    comments: [
      {
        a: 'Perfect Corp.',
        t:
          'The information of your product.The recommended number of characters is 500.',
      },
    ],
    isRequired: false,
    key: 'productDescription',
  },
  {
    column: 'F',
    text: 'Product Image*',
    comments: [
      {
        a: 'Perfect Corp.',
        t:
          'Required. Can be image URL or image file path e.g. zip/images/image.png or .jpg\n\nSize:350W X 350H',
      },
    ],
    isRequired: true,
    key: 'productImage',
  },
];

const productBasicInfoForEdit = [
  {
    column: 'A',
    text: 'Product ID',
    comments: [
      {
        a: 'Perfect Corp.',
        t: "Unique ID in Online Service Skincare Pro.\nIt's unchangeable.",
      },
    ],
    isRequired: true,
    accessor: 'id',
    key: 'id',
  },
  {
    column: 'B',
    text: 'Category*',
    comments: [{a: 'Perfect Corp.', t: 'Required. The type of your product.'}],
    isRequired: true,
    accessor: (allCategories, v) => {
      if (!v || !v.categoryIds || !Array.isArray(v.categoryIds)) return '';
      if (!allCategories || !Array.isArray(allCategories)) return '';

      const result = v.categoryIds.reduce((arr, categoryId) => {
        const category = allCategories.find(c => c.value === categoryId);

        if (category && category.label) {
          return [...arr, category.label];
        }
        return arr;
      }, []);

      if (!result || !result.length) return '';
      return result.join(',');
    },
    key: 'category',
  },
  {
    column: 'C',
    text: 'Action URL',
    comments: [{a: 'Perfect Corp.', t: 'Optional.The link to your product.'}],
    isRequired: false,
    accessor: v => {
      if (!v || !v.data || !v.data.i18n) return '';
      const r = _findKey(v.data.i18n, vv => vv && vv.isDefault);
      if (!r || !v.data.i18n[r]) return '';
      return v.data.i18n[r].actionUrl || '';
    },
    key: 'actionUrl',
  },
  {
    column: 'D',
    text: 'Default Language*',
    comments: [
      {
        a: 'Perfect Corp.',
        t:
          'Required. The default language will be displayed when the product name and description have not been translated into the language selected by the customer.',
      },
    ],
    isRequired: true,
    accessor: v => {
      if (!v || !v.data || !v.data.i18n) return '';
      const defaultLangValue = _findKey(v.data.i18n, vv => vv && vv.isDefault);
      if (!defaultLangValue) return '';

      const lang = langUtils
        .getAppLangList()
        .find(l => l.value === defaultLangValue);
      return lang ? lang.label : defaultLangValue;
    },
    key: 'defaultLanguage',
  },
  {
    column: 'E',
    text: 'Product Name*',
    comments: [
      {
        a: 'Perfect Corp.',
        t:
          'Required. The name of your product. The maximum character limit is 200.',
      },
    ],
    isRequired: true,
    accessor: 'name',
    key: 'productName',
  },

  {
    column: 'F',
    text: 'Product Description',
    comments: [
      {
        a: 'Perfect Corp.',
        t:
          'The information of your product.The recommended number of characters is 500.',
      },
    ],
    isRequired: false,
    accessor: v => {
      if (!v || !v.data || !v.data.i18n) return '';
      const r = _findKey(v.data.i18n, vv => vv && vv.isDefault);
      if (!r || !v.data.i18n[r]) return '';
      return v.data.i18n[r].productDesc || '';
    },
    key: 'productDescription',
  },
  {
    column: 'G',
    text: 'Product Image*',
    comments: [
      {
        a: 'Perfect Corp.',
        t:
          'Required. Can be image URL or image file path e.g. zip/images/image.png or .jpg\n\nSize:350W X 350H',
      },
    ],
    isRequired: true,
    accessor: 'imageUrl',
    key: 'productImage',
  },
];

const productLocalizationForEdit = [
  {
    column: 'A',
    text: 'Product ID',
    comments: [
      {
        a: 'Perfect Corp.',
        t: "Unique ID in Online Service Skincare Pro.\nIt's unchangeable.",
      },
    ],
    isRequired: true,
    accessor: 'id',
    key: 'id',
  },
  {
    column: 'B',
    text: 'Language of Name / Description*',
    comments: [
      {
        a: 'Perfect Corp.',
        t:
          'Required. You can add and display multiple language versions of the product name and description for the same Product ID.',
      },
    ],
    isRequired: true,
    accessor: (v, k) => {
      const lang = langUtils.getAppLangList().find(l => l.value === k);
      return lang ? lang.label : k;
    },
    key: 'language',
  },
  {
    column: 'C',
    text: 'Product Name*',
    comments: [
      {
        a: 'Perfect Corp.',
        t:
          'Required. The name of your product. The maximum character limit is 200.',
      },
    ],
    isRequired: true,
    accessor: (v, k) => {
      if (!v || !v.data || !v.data.i18n || !v.data.i18n[k]) return '';
      return v.data.i18n[k].productName || '';
    },
    key: 'productName',
  },
  {
    column: 'D',
    text: 'Product Description',
    comments: [
      {
        a: 'Perfect Corp.',
        t:
          'The information of your product.The recommended number of characters is 500.',
      },
    ],
    isRequired: false,
    accessor: (v, k) => {
      if (!v || !v.data || !v.data.i18n || !v.data.i18n[k]) return '';
      return v.data.i18n[k].productDesc || '';
    },
    key: 'productDescription',
  },
];

const treatmentBasicInfoForAdd = [
  {
    column: 'A',
    text: 'Category*',
    comments: [
      {a: 'Perfect Corp.', t: 'Required. The type of your treatment.'},
    ],
    isRequired: true,
    key: 'category',
  },
  {
    column: 'B',
    text: 'Action URL',
    comments: [
      {a: 'Perfect Corp.', t: 'Optional. The link to your treatment.'},
    ],
    isRequired: false,
    key: 'actionUrl',
  },
  {
    column: 'C',
    text: 'Default Language*',
    comments: [
      {
        a: 'Perfect Corp.',
        t:
          'Required. The default language will be displayed when the treatment name and description have not been translated into the language selected by the customer.',
      },
    ],
    isRequired: true,
    key: 'defaultLanguage',
  },
  {
    column: 'D',
    text: 'Treatment Name*',
    comments: [
      {
        a: 'Perfect Corp.',
        t:
          'Required. The name of your treatment. The maximum character limit is 200.',
      },
    ],
    isRequired: true,
    key: 'productName',
  },

  {
    column: 'E',
    text: 'Treatment Description',
    comments: [
      {
        a: 'Perfect Corp.',
        t:
          'The information of your treatment.The recommended number of characters is 500.',
      },
    ],
    isRequired: false,
    key: 'productDescription',
  },
  {
    column: 'F',
    text: 'Product Image*',
    comments: [
      {
        a: 'Perfect Corp.',
        t:
          'Required. Can be image URL or image file path e.g. zip/images/image.png or .jpg\n\nSize:350W X 350H',
      },
    ],
    isRequired: true,
    key: 'productImage',
  },
];

const treatmentBasicInfoForEdit = [
  {
    column: 'A',
    text: 'Treatment ID',
    comments: [
      {
        a: 'Perfect Corp.',
        t: "Unique ID in Online Service Skincare Pro.\nIt's unchangeable.",
      },
    ],
    isRequired: true,
    accessor: 'id',
    key: 'id',
  },
  {
    column: 'B',
    text: 'Category*',
    comments: [
      {a: 'Perfect Corp.', t: 'Required. The type of your treatment.'},
    ],
    isRequired: true,
    accessor: (allCategories, v) => {
      if (!v || !v.categoryIds || !Array.isArray(v.categoryIds)) return '';
      if (!allCategories || !Array.isArray(allCategories)) return '';

      const result = v.categoryIds.reduce((arr, categoryId) => {
        const category = allCategories.find(c => c.value === categoryId);

        if (category && category.label) {
          return [...arr, category.label];
        }
        return arr;
      }, []);

      if (!result || !result.length) return '';
      return result.join(',');
    },
    key: 'category',
  },
  {
    column: 'C',
    text: 'Action URL',
    comments: [{a: 'Perfect Corp.', t: 'Optional.The link to your treatment.'}],
    isRequired: false,
    accessor: v => {
      if (!v || !v.data || !v.data.i18n) return '';
      const r = _findKey(v.data.i18n, vv => vv && vv.isDefault);
      if (!r || !v.data.i18n[r]) return '';
      return v.data.i18n[r].actionUrl || '';
    },
    key: 'actionUrl',
  },
  {
    column: 'D',
    text: 'Default Language*',
    comments: [
      {
        a: 'Perfect Corp.',
        t:
          'Required. The default language will be displayed when the treatment name and description have not been translated into the language selected by the customer.',
      },
    ],
    isRequired: true,
    accessor: v => {
      if (!v || !v.data || !v.data.i18n) return '';
      const defaultLangValue = _findKey(v.data.i18n, vv => vv && vv.isDefault);
      if (!defaultLangValue) return '';

      const lang = langUtils
        .getAppLangList()
        .find(l => l.value === defaultLangValue);
      return lang ? lang.label : defaultLangValue;
    },
    key: 'defaultLanguage',
  },
  {
    column: 'E',
    text: 'Treatment Name*',
    comments: [
      {
        a: 'Perfect Corp.',
        t:
          'Required. The name of your treatment. The maximum character limit is 200.',
      },
    ],
    isRequired: true,
    accessor: 'name',
    key: 'productName',
  },

  {
    column: 'F',
    text: 'Treatment Description',
    comments: [
      {
        a: 'Perfect Corp.',
        t:
          'The information of your treatment.The recommended number of characters is 500.',
      },
    ],
    isRequired: false,
    accessor: v => {
      if (!v || !v.data || !v.data.i18n) return '';
      const r = _findKey(v.data.i18n, vv => vv && vv.isDefault);
      if (!r || !v.data.i18n[r]) return '';
      return v.data.i18n[r].productDesc || '';
    },
    key: 'productDescription',
  },
  {
    column: 'G',
    text: 'Treatment Image*',
    comments: [
      {
        a: 'Perfect Corp.',
        t:
          'Required. Can be image URL or image file path e.g. zip/images/image.png or .jpg\n\nSize:350W X 350H',
      },
    ],
    isRequired: true,
    accessor: 'imageUrl',
    key: 'productImage',
  },
];

const treatmentLocalizationForEdit = [
  {
    column: 'A',
    text: 'Treatment ID',
    comments: [
      {
        a: 'Perfect Corp.',
        t: "Unique ID in Online Service Skincare Pro.\nIt's unchangeable.",
      },
    ],
    isRequired: true,
    accessor: 'id',
    key: 'id',
  },
  {
    column: 'B',
    text: 'Language of Name / Description*',
    comments: [
      {
        a: 'Perfect Corp.',
        t:
          'Required. You can add and display multiple language versions of the treatment name and description for the same Treatment ID.',
      },
    ],
    isRequired: true,
    accessor: (v, k) => {
      const lang = langUtils.getAppLangList().find(l => l.value === k);
      return lang ? lang.label : k;
    },
    key: 'language',
  },
  {
    column: 'C',
    text: 'Treatment Name*',
    comments: [
      {
        a: 'Perfect Corp.',
        t:
          'Required. The name of your treatment. The maximum character limit is 200.',
      },
    ],
    isRequired: true,
    accessor: (v, k) => {
      if (!v || !v.data || !v.data.i18n || !v.data.i18n[k]) return '';
      return v.data.i18n[k].productName || '';
    },
    key: 'productName',
  },
  {
    column: 'D',
    text: 'Treatment Description',
    comments: [
      {
        a: 'Perfect Corp.',
        t:
          'The information of your treatment.The recommended number of characters is 500.',
      },
    ],
    isRequired: false,
    accessor: (v, k) => {
      if (!v || !v.data || !v.data.i18n || !v.data.i18n[k]) return '';
      return v.data.i18n[k].productDesc || '';
    },
    key: 'productDescription',
  },
];

const allFileTypes = [
  {type: 'application/vnd.ms-excel', extension: 'excel'},
  {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    extension: 'excel',
  },
  {type: 'application/x-zip-compressed', extension: 'zip'},
  {type: 'application/octet-stream', extension: 'zip'},
  {type: 'multipart/x-zip', extension: 'zip'},
  {type: 'application/zip', extension: 'zip'},
  {type: 'application/zip-compressed', extension: 'zip'},
];

async function urlToDataUrl(url) {
  const urlString =
    typeof url === 'object' && url && url.default ? url.default : url;
  const res = await fetch(`${urlString}?p=${new Date().getTime()}`);
  const blob = await res.blob();
  return await readImgDataUrl(blob);
}

async function readImgDataUrl(blob) {
  const reader = new FileReader();
  reader.readAsDataURL(blob);
  return await new Promise((resolve, reject) => {
    reader.onerror = () => {
      reader.abort();
      reject('');
    };
    reader.onload = () => {
      resolve(reader.result);
    };
  });
}

const getFileType = file => {
  const r = _find(allFileTypes, {type: file.type});
  return r ? r.extension : undefined;
};

function getHeader(isEdit, type = 'product') {
  if (type === 'treatment') {
    return isEdit
      ? {
          basicInfo: treatmentBasicInfoForEdit,
          localization: treatmentLocalizationForEdit,
        }
      : {
          basicInfo: treatmentBasicInfoForAdd,
        };
  }

  return isEdit
    ? {
        basicInfo: productBasicInfoForEdit,
        localization: productLocalizationForEdit,
      }
    : {
        basicInfo: productBasicInfoForAdd,
      };
}

function getSheetRows(ref) {
  const arr = ref.split(':');
  const r = arr.map(rr => {
    const regex = /^[a-zA-Z]{1}(.*)/g;
    const match = regex.exec(rr);
    return match[1];
  });
  return {begin: parseInt(r[0]), end: parseInt(r[1])};
}

function calculateActualDataRows(sheetData, begin, end) {
  let actualDataRows = 0;
  for (let i = begin + 1; i <= end; i++) {
    const rowData = sheetData[i - 1];
    if (!rowData) continue;

    const hasData = Object.keys(rowData).some(key => {
      const value = rowData[key];
      return (
        value !== null && value !== undefined && value.toString().trim() !== ''
      );
    });

    if (hasData) actualDataRows++;
  }
  return actualDataRows;
}

function isValidCategory(category, categories) {
  if (!category) return false;
  return categories.some(cat => cat.label === category);
}

function isValidActionUrl(url) {
  if (!url || 0 === url.length) return false;
  const urlStr = url.toString();
  if (urlStr.startsWith('http')) return /https?:\/\/.*/g.test(urlStr);
  return /[ymk|ycp|ymkbc|yct|y4b]:\/\/.*/g.test(urlStr);
}

function isValidLanguage(language) {
  const validLanguages = langUtils.getAppLangList().map(lang => lang.label);
  return language ? validLanguages.includes(language) : false;
}

function isValidImageUrl(imageUrl) {
  if (!imageUrl || 0 === imageUrl.length) return false;
  if (!imageUrl.startsWith('http') || !imageUrl.startsWith('https'))
    return false;
  const validImageExtension = ['jpg', 'png'];
  const url = imageUrl.replace(/\?.*/, '');
  const extension = url.substring(url.lastIndexOf('.') + 1);
  return validImageExtension.includes(extension);
}

async function getDataFromExcel(blob) {
  return new Promise(resolve => {
    const fr = new FileReader();
    fr.onload = e => {
      const data = new Uint8Array(e.target.result);
      resolve(data);
    };
    fr.readAsArrayBuffer(blob);
  });
}

async function uploadImage(imageUrl, token, customerId) {
  try {
    const dataUrl = await urlToDataUrl(imageUrl);
    const file = dataURLtoFile(dataUrl, 'productImg');

    const result = await uploadFile(
      '',
      file,
      350,
      350,
      'SkuImage',
      token,
      customerId,
      true
    );

    if (!result || !result.fileId) {
      return {error: 'UploadError', details: result};
    }

    return {fileId: result.fileId, originalUrl: result.originalUrl};
  } catch (error) {
    console.error('Fail to upload image:', error);
    return {error: 'UploadError', details: error.message};
  }
}

async function uploadImageFromFile(imageUrl, imageBlob, token, customerId) {
  // handle zip file with http imageUrl
  if (imageUrl.startsWith('http') || imageUrl.startsWith('https')) {
    return await uploadImage(imageUrl, token, customerId);
  }

  return imageBlob.then(async res => {
    try {
      const extension = imageUrl.substring(imageUrl.lastIndexOf('.') + 1);
      const blob = new Blob([res], {type: `image/${extension}`});
      const file = new File([blob], `productImg.${extension}`, {
        type: `image/${extension}`,
      });
      const result = await uploadFile(
        '',
        file,
        350,
        350,
        'SkuImage',
        token,
        customerId,
        true
      );

      if (!result || !result.fileId) {
        return {error: 'UploadError', details: result};
      }

      return {fileId: result.fileId, originalUrl: result.originalUrl};
    } catch (error) {
      console.error('Fail to upload image from file:', error);
      return {error: 'UploadError', details: error.message};
    }
  });
}

async function checkImageDimension(imageUrl) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      resolve({width: img.naturalWidth, height: img.naturalHeight});
    };
    img.onerror = () => {
      reject(new Error(`Failed to load image: ${imageUrl}`));
    };
    img.src = imageUrl;
  });
}

async function buildCompleteObj(
  sheets,
  isEdit,
  validProductIds,
  categories,
  brandName,
  imageInfoMap,
  actualDataRows,
  type = 'product'
) {
  return isEdit
    ? buildCompleteObjForEdit(
        sheets,
        validProductIds,
        categories,
        brandName,
        imageInfoMap,
        actualDataRows,
        type
      )
    : buildCompleteObjForAdd(
        sheets,
        categories,
        brandName,
        imageInfoMap,
        actualDataRows,
        type
      );
}

async function buildCompleteObjForAdd(
  sheets,
  categories,
  brandName,
  imageInfoMap,
  actualDataRows,
  type = 'product'
) {
  const objs = [];
  const basicData = XLSX.utils.sheet_to_json(sheets['Basic Info'], {header: 1});

  const nameColumnName =
    type === 'treatment' ? 'Treatment Name*' : 'Product Name*';
  const descColumnName =
    type === 'treatment' ? 'Treatment Description' : 'Product Description';

  const categoryIndex = basicData[0].indexOf('Category*');
  const actionUrlIndex = basicData[0].indexOf('Action URL');
  const languageIndex = basicData[0].indexOf('Default Language*');
  const productNameIndex = basicData[0].indexOf(nameColumnName);
  const productDescIndex = basicData[0].indexOf(descColumnName);

  const {begin} = getSheetRows(sheets['Basic Info']['!ref']);
  const actualEnd = begin + actualDataRows;

  for (let rowIndex = begin + 1; rowIndex <= actualEnd; rowIndex++) {
    const row = basicData[rowIndex - 1];
    if (!row || row.length === 0) continue;

    // categoryId mapping
    const categoryValue = row[categoryIndex].toString().trim();
    let categoryName = '';
    let categoryId = null;

    if (categoryValue) {
      const category = categories.find(cat => cat.label === categoryValue);
      if (category) {
        categoryName = category.label;
        categoryId = category.value;
      }
    }

    const i18n = {};
    const defaultLangLabel = row[languageIndex].toString().trim();
    const defaultLang = langUtils
      .getAppLangList()
      .find(lang => lang.label === defaultLangLabel);

    if (!defaultLang) {
      console.error(`Invalid language at row ${rowIndex}: ${defaultLangLabel}`);
      continue;
    }

    const defaultLangValue = defaultLang.value;

    i18n[defaultLangValue] = {
      isDefault: true,
      productName: row[productNameIndex].toString().trim(),
      productDesc: row[productDescIndex]
        ? row[productDescIndex].toString().trim()
        : '',
      actionUrl: row[actionUrlIndex] ? row[actionUrlIndex].toString() : '',
      brandName: brandName,
    };

    const imageInfo = imageInfoMap.get(rowIndex);

    if (!imageInfo) {
      console.error(`No image info found for row ${rowIndex}`);
      continue;
    }

    const obj = {
      categoryIds: categoryId,
      data: {
        productSource: 'BRAND',
        i18n,
        files: [
          {
            displayOrder: 1000,
            imageType: 'PRODUCT_IMAGE',
            file: {
              id: imageInfo.fileId,
              url: imageInfo.imageUrl,
            },
          },
        ],
      },
    };

    objs.push(obj);
  }

  return {objs};
}

async function buildCompleteObjForEdit(
  sheets,
  validProductIds,
  categories,
  brandName,
  imageInfoMap,
  actualDataRows,
  type = 'product'
) {
  const objs = [];
  const basicData = XLSX.utils.sheet_to_json(sheets['Basic Info'], {header: 1});

  const localizationSheetName =
    type === 'treatment' ? 'Treatment Localization' : 'Product Localization';
  const idColumnName = type === 'treatment' ? 'Treatment ID' : 'Product ID';
  const nameColumnName =
    type === 'treatment' ? 'Treatment Name*' : 'Product Name*';
  const descColumnName =
    type === 'treatment' ? 'Treatment Description' : 'Product Description';

  const localizationData = XLSX.utils.sheet_to_json(
    sheets[localizationSheetName],
    {header: 1}
  );

  const bi_productIdIndex = basicData[0].indexOf(idColumnName);
  const bi_categoryIndex = basicData[0].indexOf('Category*');
  const bi_actionUrlIndex = basicData[0].indexOf('Action URL');
  const bi_defaultLanguageIndex = basicData[0].indexOf('Default Language*');
  const bi_productNameIndex = basicData[0].indexOf(nameColumnName);
  const bi_productDescIndex = basicData[0].indexOf(descColumnName);

  const pl_productIdIndex = localizationData[0].indexOf(idColumnName);
  const pl_languageIndex = localizationData[0].indexOf(
    'Language of Name / Description*'
  );
  const pl_productNameIndex = localizationData[0].indexOf(nameColumnName);
  const pl_productDescIndex = localizationData[0].indexOf(descColumnName);

  const {begin} = getSheetRows(sheets['Basic Info']['!ref']);
  const actualEnd = begin + actualDataRows;

  for (let rowIndex = begin + 1; rowIndex <= actualEnd; rowIndex++) {
    const row = basicData[rowIndex - 1];
    if (!row || row.length === 0) continue;

    const productId = row[bi_productIdIndex].toString().trim();
    if (!validProductIds.has(productId)) continue;

    const categoryValue = row[bi_categoryIndex].toString().trim();
    let categoryName = '';
    let categoryId = null;

    if (categoryValue) {
      const category = categories.find(cat => cat.label === categoryValue);
      if (category) {
        categoryName = category.label;
        categoryId = category.value;
      }
    }

    const defaultLangLabel = row[bi_defaultLanguageIndex].toString().trim();
    const defaultLang = langUtils
      .getAppLangList()
      .find(lang => lang.label === defaultLangLabel);

    if (!defaultLang) {
      console.error(
        `Invalid default language at row ${rowIndex}: ${defaultLangLabel}`
      );
      continue;
    }

    const defaultLangValue = defaultLang.value;

    const i18n = {};

    i18n[defaultLangValue] = {
      isDefault: true,
      productName: row[bi_productNameIndex].toString().trim(),
      productDesc: row[bi_productDescIndex]
        ? row[bi_productDescIndex].toString().trim()
        : '',
      actionUrl: row[bi_actionUrlIndex]
        ? row[bi_actionUrlIndex].toString()
        : '',
      brandName: brandName,
    };

    localizationData
      .filter((locRow, index) => {
        if (index === 0 || !locRow) return false;
        const locProductId = locRow[pl_productIdIndex].toString().trim();
        return locProductId === productId;
      })
      .forEach(locRow => {
        const langLabel = locRow[pl_languageIndex].toString().trim();
        const lang = langUtils
          .getAppLangList()
          .find(l => l.label === langLabel);

        if (!lang) {
          console.error(`Invalid language: ${langLabel}`);
          return;
        }

        const langValue = lang.value;

        if (i18n[langValue]) return;

        i18n[langValue] = {
          isDefault: false,
          productName: locRow[pl_productNameIndex].toString().trim(),
          productDesc: locRow[pl_productDescIndex]
            ? locRow[pl_productDescIndex].toString().trim()
            : '',
          actionUrl: row[bi_actionUrlIndex]
            ? row[bi_actionUrlIndex].toString()
            : '',
          brandName: brandName,
        };
      });

    const imageInfo = imageInfoMap.get(rowIndex);

    if (!imageInfo) {
      console.error(`No image info found for row ${rowIndex}`);
      continue;
    }

    const obj = {
      productId: productId,
      categoryIds: categoryId,
      data: {
        productSource: 'BRAND',
        i18n,
        files: [
          {
            displayOrder: 1000,
            imageType: 'PRODUCT_IMAGE',
            file: {
              id: imageInfo.fileId,
              url: imageInfo.imageUrl,
            },
          },
        ],
      },
    };

    objs.push(obj);
  }

  return {objs};
}

export async function importProducts(
  file,
  type = 'product',
  isEdit = false,
  productCount,
  totalQuota,
  allCategories = [],
  brandName,
  token,
  customerId
) {
  try {
    const header = getHeader(isEdit, type);
    const fileType = getFileType(file);
    if (!fileType) {
      return {
        errorType: 'file',
        errorMessage: 'Invalid file. Please upload a valid file.',
        errors: [],
      };
    }

    // Process excel or zip file
    let zip, excelFile, workbook;
    const imageMap = {};
    if (fileType === 'excel') {
      excelFile = await getDataFromExcel(file);
    } else {
      zip = await JSZip.loadAsync(file);
      if (zip.files.length === 0) {
        return {
          errorType: 'file',
          errorMessage: 'Invalid file. Please upload a valid file.',
          errors: [],
        };
      }

      const excel = await Object.values(zip.files).find(f =>
        new RegExp(/.*[.](xlsx)$/g).test(f.name)
      );
      if (!excel) {
        return {
          errorType: 'file',
          errorMessage: 'Invalid file. Please upload a valid file.',
          errors: [],
        };
      }
      excelFile = await excel.async('uint8array');
      const images = await Object.values(zip.files).filter(f =>
        new RegExp(/.*[.](jpg|png)$/g).test(f.name)
      );
      images.forEach(i => {
        // Extract filename from path
        const fileName = i.name.split('/').pop();
        imageMap[fileName] = i.async('uint8array');
      });
    }

    try {
      workbook = XLSX.read(excelFile, {type: 'array'});
    } catch (xlsxError) {
      return {
        errorType: 'file',
        errorMessage: `Failed to read Excel file: ${xlsxError.message}`,
        errors: [],
      };
    }

    // Column header checking
    const headerCheck = await validateColumnHeader(
      workbook,
      header,
      isEdit,
      type
    );

    if (!headerCheck.isValid) {
      return {
        errorType: 'header',
        errorMessage: 'Invalid header.',
        errors: headerCheck.errors,
      };
    }

    // Quota checking
    const quotaCheck = await validateQuota(workbook, productCount, totalQuota);

    if (!quotaCheck.isValid) {
      return {
        errorType: 'quota',
        errorMessage: 'Not enough quota.',
        errors: quotaCheck.errors,
      };
    }

    const actualDataRows = quotaCheck.actualDataRows;

    // Preview data checking
    const previewCheck = await validateFilePreview(
      workbook,
      header,
      imageMap,
      isEdit,
      allCategories,
      brandName,
      token,
      customerId,
      actualDataRows,
      type
    );

    if (!previewCheck.isValid) {
      return {
        errorType: 'preview',
        errorMessage: 'Invalid preview.',
        errors: previewCheck.errors,
        completeData: previewCheck.completeData,
      };
    } else {
      return {completeData: previewCheck.completeData};
    }
  } catch (e) {
    console.error('Fail to import products', e);
    return {
      errorType: 'file',
      errorMessage: `Exception while uploading files. ${e}`,
      errors: [],
    };
  }
}

async function validateColumnHeader(
  workbook,
  header,
  isEdit,
  type = 'product'
) {
  const errors = isEdit
    ? {
        basicInfo: {
          missing: [],
          unsupported: [],
          firstRow: [],
          noSheet: false,
        },
        localization: {
          missing: [],
          unsupported: [],
          firstRow: [],
          noSheet: false,
        },
      }
    : {
        basicInfo: {
          missing: [],
          unsupported: [],
          firstRow: [],
          noSheet: false,
        },
      };

  const sheetsToValidate = isEdit
    ? ['basicInfo', 'localization']
    : ['basicInfo'];

  const sheets = workbook.Sheets;

  sheetsToValidate.forEach(key => {
    const sheetName =
      key === 'basicInfo'
        ? 'Basic Info'
        : key === 'localization'
        ? type === 'treatment'
          ? 'Treatment Localization'
          : 'Product Localization'
        : key;

    // check missing sheet
    if (!sheetName || !sheets[sheetName]) {
      errors[key].noSheet = true;
      return;
    }

    const sheetCols = [];
    Object.keys(sheets[sheetName]).forEach(cell => {
      const cellComment = _get(sheets[sheetName][cell], 'c', null);
      if (!cellComment) return;

      // check header in first row
      const rowNum = cell.substring(1, cell.length).toString();
      if (rowNum !== '1') {
        errors[key].firstRow.push(cell);
      }

      const cellValue = _get(sheets[sheetName][cell], 'v', null);
      if (!cellValue) return;
      sheetCols.push(cellValue.toString());
    });

    // check missing columns
    header[key].forEach(col => {
      if (!sheetCols.includes(col.text)) {
        errors[key].missing.push(col.text);
      }
    });

    // check unsupported columns
    sheetCols.forEach(col => {
      const headerMatch = _find(header[key], {text: col});
      if (!headerMatch) {
        errors[key].unsupported.push(col);
      }
    });
  });

  let errorCount = 0;
  Object.keys(errors).forEach(key => {
    errorCount +=
      errors[key].missing.length +
      errors[key].unsupported.length +
      errors[key].firstRow.length +
      (errors[key].noSheet ? 1 : 0);
  });

  return {isValid: errorCount === 0, errors};
}

async function validateQuota(workbook, productCount, totalQuota) {
  const errors = {notEnoughQuota: 0};
  const sheets = workbook.Sheets;
  const sheet = sheets['Basic Info'];

  // Calculate actual data rows (skip header)
  const sheetData = XLSX.utils.sheet_to_json(sheet, {header: 'A'});
  const {begin, end} = getSheetRows(sheet['!ref']);

  const actualDataRows = calculateActualDataRows(sheetData, begin, end);

  if (actualDataRows + productCount > totalQuota) {
    errors.notEnoughQuota = actualDataRows + productCount - totalQuota;
  }

  return {
    isValid: errors.notEnoughQuota === 0,
    errors,
    actualDataRows,
  };
}

async function validateFilePreview(
  workbook,
  header,
  imageMap,
  isEdit,
  categories,
  brandName,
  token,
  customerId,
  actualDataRows,
  type = 'product'
) {
  return isEdit
    ? validateFilePreviewForEdit(
        workbook,
        header,
        imageMap,
        categories,
        brandName,
        token,
        customerId,
        actualDataRows,
        type
      )
    : validateFilePreviewForAdd(
        workbook,
        header,
        imageMap,
        categories,
        brandName,
        token,
        customerId,
        actualDataRows,
        type
      );
}

async function validateFilePreviewForAdd(
  workbook,
  header,
  imageMap = null,
  categories,
  brandName,
  token,
  customerId,
  actualDataRows,
  type = 'product'
) {
  const errors = {
    basicInfo: {
      rows: 0,
      noData: [],
      isBlank: [],
      invalidActionUrl: [],
      invalidCategory: [],
      invalidLanguage: [],
      invalidImageUrl: [],
      imageDimension: [],
      imageUpload: [],
      imageSize: [],
      productNameLength: [],
      productDescLength: [],
    },
    category: {isBlank: [], invalidCategory: [], empty: []},
    language: {isBlank: [], invalidLanguage: [], empty: []},
  };
  const sheets = workbook.Sheets;

  // Step 1: Basic Info data validation
  errors.basicInfo = validateBasicInfoSheet(
    false,
    sheets,
    header,
    categories,
    imageMap,
    actualDataRows,
    type
  );

  // Step 2: Category & Language data validation
  errors.category = validateCategorySheet(sheets, categories);

  errors.language = validateLanguageSheet(sheets);

  // Step 3: Calculate error count
  let errorCount = 0;
  Object.keys(errors['basicInfo'])
    .filter(key => key !== 'rows')
    .forEach(e => {
      errorCount += errors['basicInfo'][e].length;
    });
  errorCount += errors['category'].isBlank.length;
  errorCount += errors['category'].invalidCategory.length;
  errorCount += errors['category'].empty.length;
  errorCount += errors['category'].noSheet ? 1 : 0;
  errorCount += errors['language'].isBlank.length;
  errorCount += errors['language'].invalidLanguage.length;
  errorCount += errors['language'].empty.length;
  errorCount += errors['language'].noSheet ? 1 : 0;

  if (errorCount > 0) {
    return {isValid: false, errors, completeData: null};
  }

  // Step 4: Image dimension validation
  const imageValidationResult = await validateProductImage(
    sheets,
    imageMap,
    token,
    customerId,
    actualDataRows,
    type
  );

  if (!imageValidationResult.isValid) {
    errors.basicInfo.imageDimension =
      imageValidationResult.invalidImageDimension;
    errors.basicInfo.imageUpload = imageValidationResult.invalidImageUpload;
    errorCount +=
      imageValidationResult.invalidImageDimension.length +
      imageValidationResult.invalidImageUpload.length;
    return {isValid: false, errors, completeData: null};
  }

  const imageInfoMap = imageValidationResult.imageInfoMap;

  // Step 5: All validations passed, prepare complete data
  const completeData = await buildCompleteObj(
    sheets,
    false,
    null,
    categories,
    brandName,
    imageInfoMap,
    actualDataRows,
    type
  );

  return {isValid: true, errors, completeData: completeData.objs};
}

async function validateFilePreviewForEdit(
  workbook,
  header,
  imageMap = null,
  categories,
  brandName,
  token,
  customerId,
  actualDataRows,
  type = 'product'
) {
  const errors = {
    basicInfo: {
      rows: 0,
      noData: [],
      isBlank: [],
      invalidActionUrl: [],
      invalidCategory: [],
      invalidLanguage: [],
      invalidImageUrl: [],
      imageDimension: [],
      imageUpload: [],
      imageSize: [],
      productNameLength: [],
      productDescLength: [],
      duplicateProductId: [],
    },
    localization: {
      rows: 0,
      noData: [],
      isBlank: [],
      invalidProductId: [],
      invalidLanguage: [],
      productNameLength: [],
      productDescLength: [],
    },
  };

  const sheets = workbook.Sheets;

  // Step 1: Basic Info data validation
  const basicInfoResult = validateBasicInfoSheet(
    true,
    sheets,
    header,
    categories,
    imageMap,
    actualDataRows,
    type
  );

  const {validProductIds, ...basicInfoErrors} = basicInfoResult;
  errors.basicInfo = basicInfoErrors;

  // Step 2: Product/Treatment Localization data validation
  errors.localization = validateLocalizationSheet(
    sheets,
    header,
    validProductIds,
    type
  );

  // Step 3: Category & Language data validation
  errors.category = validateCategorySheet(sheets, categories);

  errors.language = validateLanguageSheet(sheets);

  // Step 4: Calculate error count
  let errorCount = 0;
  ['basicInfo', 'localization'].forEach(sheetKey => {
    Object.keys(errors[sheetKey])
      .filter(key => key !== 'rows')
      .forEach(e => {
        errorCount += errors[sheetKey][e].length;
      });
  });
  errorCount += errors['category'].isBlank.length;
  errorCount += errors['category'].invalidCategory.length;
  errorCount += errors['category'].empty.length;
  errorCount += errors['category'].noSheet ? 1 : 0;
  errorCount += errors['language'].isBlank.length;
  errorCount += errors['language'].invalidLanguage.length;
  errorCount += errors['language'].empty.length;
  errorCount += errors['language'].noSheet ? 1 : 0;

  if (errorCount > 0) {
    return {isValid: false, errors, completeData: null};
  }

  // Step 5: Image dimension validation
  const imageValidationResult = await validateProductImage(
    sheets,
    imageMap,
    token,
    customerId,
    actualDataRows,
    type
  );

  if (!imageValidationResult.isValid) {
    errors.basicInfo.imageDimension =
      imageValidationResult.invalidImageDimension;
    errors.basicInfo.imageUpload = imageValidationResult.invalidImageUpload;
    errorCount +=
      imageValidationResult.invalidImageDimension.length +
      imageValidationResult.invalidImageUpload.length;
    return {isValid: false, errors, completeData: null};
  }

  const imageInfoMap = imageValidationResult.imageInfoMap;

  // Step 6: All validations passed, prepare complete data
  const completeData = await buildCompleteObj(
    sheets,
    true,
    validProductIds,
    categories,
    brandName,
    imageInfoMap,
    actualDataRows,
    type
  );

  return {isValid: true, errors, completeData: completeData.objs};
}

function validateBasicInfoSheet(
  isEdit,
  sheets,
  header,
  categories,
  imageMap,
  actualDataRows,
  type = 'product'
) {
  const errors = isEdit
    ? {
        rows: 0,
        noData: [],
        isBlank: [],
        invalidActionUrl: [],
        invalidCategory: [],
        invalidLanguage: [],
        invalidImageUrl: [],
        productNameLength: [],
        productDescLength: [],
        duplicateProductId: [],
      }
    : {
        rows: 0,
        noData: [],
        isBlank: [],
        invalidActionUrl: [],
        invalidCategory: [],
        invalidLanguage: [],
        invalidImageUrl: [],
        productNameLength: [],
        productDescLength: [],
      };

  const basicSheetName = 'Basic Info';

  const idColumnName = type === 'treatment' ? 'Treatment ID' : 'Product ID';
  const nameColumnName =
    type === 'treatment' ? 'Treatment Name*' : 'Product Name*';
  const descColumnName =
    type === 'treatment' ? 'Treatment Description' : 'Product Description';
  const imageColumnName =
    type === 'treatment' ? 'Treatment Image*' : 'Product Image*';

  let productIdColumn = 'A';
  let categoryColumn = isEdit ? 'B' : 'A';
  let actionUrlColumn = isEdit ? 'C' : 'B';
  let defaultLanguageColumn = isEdit ? 'D' : 'C';
  let productNameColumn = isEdit ? 'E' : 'D';
  let productDescColumn = isEdit ? 'F' : 'E';
  let productImageColumn = isEdit ? 'G' : 'F';

  const sheetData = XLSX.utils.sheet_to_json(sheets[basicSheetName], {
    header: 'A',
  });

  // column mapping
  Object.keys(sheetData[0]).forEach(key => {
    if (isEdit && sheetData[0][key].includes(idColumnName)) {
      productIdColumn = key;
    }
    if (sheetData[0][key].includes('Category*')) {
      categoryColumn = key;
    }
    if (sheetData[0][key].includes('Action URL')) {
      actionUrlColumn = key;
    }
    if (sheetData[0][key].includes('Default Language*')) {
      defaultLanguageColumn = key;
    }
    if (sheetData[0][key].includes(nameColumnName)) {
      productNameColumn = key;
    }
    if (sheetData[0][key].includes(descColumnName)) {
      productDescColumn = key;
    }
    if (sheetData[0][key].includes(imageColumnName)) {
      productImageColumn = key;
    }
  });

  const {begin} = getSheetRows(sheets[basicSheetName]['!ref']);

  if (actualDataRows === 0) {
    errors.noData.push('Basic Info sheet has no data rows');
    return errors;
  }

  errors.rows = actualDataRows;
  const end = begin + actualDataRows;

  let productIdSet = null;
  if (isEdit) {
    productIdSet = new Set();
    for (let i = begin + 1; i <= end; i++) {
      const cell = _get(sheets[basicSheetName], `${productIdColumn}${i}`, null);
      if (cell && cell.v) {
        const productId = cell.v.toString().trim();
        if (productIdSet.has(productId)) {
          errors.duplicateProductId.push(i);
        } else {
          productIdSet.add(productId);
        }
      }
    }
  }

  // validate each column
  Object.keys(sheets[basicSheetName]).forEach(cell => {
    const cellComment = _get(sheets[basicSheetName][cell], 'c', null);
    if (!cellComment) return;
    const cellValue = _get(sheets[basicSheetName][cell], 'v', null);
    if (!cellValue) return;

    const columnConfig = _find(header['basicInfo'], {text: cellValue});
    if (!columnConfig) return;

    // required columns
    if (columnConfig.isRequired) {
      for (let i = begin + 1; i <= end; i++) {
        const dataCell = _get(
          sheets[basicSheetName],
          `${columnConfig.column}${i}`,
          null
        );
        // no value
        if (!dataCell || !dataCell.v) {
          errors.isBlank.push(i);
          continue;
        }
        // value validations
        switch (columnConfig.text) {
          case 'Category*':
            if (!isValidCategory(dataCell.v, categories)) {
              errors.invalidCategory.push(i);
            }
            break;
          case 'Default Language*':
            if (!isValidLanguage(dataCell.v)) {
              errors.invalidLanguage.push(i);
            }
            break;
          case 'Product Name*':
          case 'Treatment Name*':
            if (dataCell.v.length > 200) {
              errors.productNameLength.push(i);
            }
            break;
          case 'Product Image*':
          case 'Treatment Image*':
            if (!imageMap || _isEmpty(imageMap)) {
              if (!isValidImageUrl(dataCell.v)) {
                errors.invalidImageUrl.push(i);
              }
            } else {
              const r = Object.keys(imageMap).filter(k => {
                return k.toLowerCase() === dataCell.v.toLowerCase();
              });

              if (r.length === 0) {
                if (!isValidImageUrl(dataCell.v)) {
                  errors.invalidImageUrl.push(i);
                }
              }
            }
            break;
          default:
            break;
        }
      }
    } else {
      // optional columns
      for (let i = begin + 1; i <= end; i++) {
        const dataCell = _get(
          sheets[basicSheetName],
          `${columnConfig.column}${i}`,
          null
        );

        if (!dataCell || !dataCell.v) {
          continue;
        }

        switch (columnConfig.text) {
          case 'Action URL':
            if (!isValidActionUrl(dataCell.v)) {
              errors.invalidActionUrl.push(i);
            }
            break;
          case 'Product Description':
          case 'Treatment Description':
            if (dataCell.v.length > 500) {
              errors.productDescLength.push(i);
            }
            break;
          default:
            break;
        }
      }
    }
  });

  return isEdit ? {...errors, validProductIds: productIdSet} : errors;
}

function validateLocalizationSheet(
  sheets,
  header,
  validProductIds,
  type = 'product'
) {
  const errors = {
    rows: 0,
    noData: [],
    isBlank: [],
    invalidProductId: [],
    invalidLanguage: [],
    productNameLength: [],
    productDescLength: [],
  };

  const localizationSheetName =
    type === 'treatment' ? 'Treatment Localization' : 'Product Localization';

  let productIdColumn = 'A';
  let languageColumn = 'B';
  let productNameColumn = 'C';
  let productDescColumn = 'D';

  const sheetData = XLSX.utils.sheet_to_json(sheets[localizationSheetName], {
    header: 'A',
  });

  // column mapping
  Object.keys(sheetData[0]).forEach(key => {
    if (
      sheetData[0][key].includes('Product ID') ||
      sheetData[0][key].includes('Treatment ID')
    ) {
      productIdColumn = key;
    }
    if (sheetData[0][key].includes('Language of Name / Description*')) {
      languageColumn = key;
    }
    if (
      sheetData[0][key].includes('Product Name*') ||
      sheetData[0][key].includes('Treatment Name*')
    ) {
      productNameColumn = key;
    }
    if (
      sheetData[0][key].includes('Product Description') ||
      sheetData[0][key].includes('Treatment Description')
    ) {
      productDescColumn = key;
    }
  });

  const {begin, end} = getSheetRows(sheets[localizationSheetName]['!ref']);

  // validate data rows (no data)
  const actualDataRows = calculateActualDataRows(sheetData, begin, end);

  if (actualDataRows === 0) {
    errors.noData.push('Product Localization sheet has no data rows');
    return errors;
  }

  errors.rows = actualDataRows;
  const actualEnd = begin + actualDataRows;

  // validate each column
  Object.keys(sheets[localizationSheetName]).forEach(cell => {
    const cellComment = _get(sheets[localizationSheetName][cell], 'c', null);
    if (!cellComment) return;
    const cellValue = _get(sheets[localizationSheetName][cell], 'v', null);
    if (!cellValue) return;

    const columnConfig = _find(header['localization'], {
      text: cellValue,
    });
    if (!columnConfig) return;

    // required columns
    if (columnConfig.isRequired) {
      for (let i = begin + 1; i <= actualEnd; i++) {
        const dataCell = _get(
          sheets[localizationSheetName],
          `${columnConfig.column}${i}`,
          null
        );
        // no value
        if (!dataCell || !dataCell.v) {
          errors.isBlank.push(i);
          continue;
        }
        // value validations
        switch (columnConfig.text) {
          case 'Product ID':
          case 'Treatment ID':
            const productId = dataCell.v.toString().trim();
            if (!validProductIds.has(productId)) {
              errors.invalidProductId.push(i);
            }
            break;
          case 'Language of Name / Description*':
            if (!isValidLanguage(dataCell.v)) {
              errors.invalidLanguage.push(i);
            }
            break;
          case 'Product Name*':
            if (dataCell.v.length > 200) {
              errors.productNameLength.push(i);
            }
            break;
          default:
            break;
        }
      }
    } else {
      // optional columns
      for (let i = begin + 1; i <= actualEnd; i++) {
        const dataCell = _get(
          sheets[localizationSheetName],
          `${columnConfig.column}${i}`,
          null
        );

        if (!dataCell || !dataCell.v) {
          continue;
        }

        if (
          columnConfig.text === 'Product Description' ||
          columnConfig.text === 'Treatment Description'
        ) {
          if (dataCell.v.length > 500) {
            errors.productDescLength.push(i);
          }
        }
      }
    }
  });

  return errors;
}

function validateCategorySheet(sheets, categories) {
  const errors = {
    isBlank: [],
    invalidCategory: [],
    empty: [],
    noSheet: false,
  };

  const categorySheetName = 'Category';

  // Check if sheet exists
  if (!sheets[categorySheetName]) {
    errors.noSheet = true;
    return errors;
  }

  // Check if sheet has data
  if (!sheets[categorySheetName]['!ref']) {
    errors.empty.push('Category sheet is empty');
    return errors;
  }

  const {begin, end} = getSheetRows(sheets[categorySheetName]['!ref']);

  // validate data rows (no data)
  let actualDataRows = 0;
  for (let i = begin; i <= end; i++) {
    const cell = sheets[categorySheetName][`A${i}`];

    if (!cell || !cell.v) continue;

    const cellValue = cell.v.toString().trim();
    if (cellValue !== '') {
      actualDataRows++;
    }
  }

  if (actualDataRows === 0) {
    errors.empty.push('Category sheet is empty');
    return errors;
  }

  // Validate each row
  for (let i = begin; i <= end; i++) {
    const cell = sheets[categorySheetName][`A${i}`];

    if (!cell || !cell.v || cell.v.toString().trim() === '') {
      errors.isBlank.push(i);
      continue;
    }

    if (!categories.some(cat => cat.label === cell.v)) {
      errors.invalidCategory.push(cell.v);
    }
  }

  return errors;
}

function validateLanguageSheet(sheets) {
  const errors = {
    isBlank: [],
    invalidLanguage: [],
    empty: [],
    noSheet: false,
  };

  const languageSheetName = 'Language';

  // Check if sheet exists
  if (!sheets[languageSheetName]) {
    errors.noSheet = true;
    return errors;
  }

  // Check if sheet has data
  if (!sheets[languageSheetName]['!ref']) {
    errors.empty.push('Language sheet is empty');
    return errors;
  }

  const supportedLanguages = langUtils.getAppLangList().map(lang => lang.label);
  const {begin, end} = getSheetRows(sheets[languageSheetName]['!ref']);

  // Validate data rows (no data)
  let actualDataRows = 0;
  for (let i = begin; i <= end; i++) {
    const cell = sheets[languageSheetName][`A${i}`];

    if (!cell || !cell.v) continue;

    const cellValue = cell.v.toString().trim();
    if (cellValue !== '') {
      actualDataRows++;
    }
  }

  if (actualDataRows === 0) {
    errors.empty.push('Language sheet is empty');
    return errors;
  }

  // Validate each row
  for (let i = begin; i <= end; i++) {
    const cell = sheets[languageSheetName][`A${i}`];

    if (!cell || !cell.v || cell.v.toString().trim() === '') {
      errors.isBlank.push(i);
      continue;
    }

    if (!supportedLanguages.includes(cell.v)) {
      errors.invalidLanguage.push(cell.v);
    }
  }

  return errors;
}

async function validateProductImage(
  sheets,
  imageMap = null,
  token,
  customerId,
  actualDataRows,
  type = 'product'
) {
  const invalidImageDimension = [];
  const invalidImageUpload = [];

  const sheetData = XLSX.utils.sheet_to_json(sheets['Basic Info'], {
    header: 'A',
  });

  const imageColumnName =
    type === 'treatment' ? 'Treatment Image*' : 'Product Image*';

  let productImageColumn = 'F';
  Object.keys(sheetData[0]).forEach(key => {
    if (sheetData[0][key] && sheetData[0][key].includes(imageColumnName)) {
      productImageColumn = key;
    }
  });
  const {begin} = getSheetRows(sheets['Basic Info']['!ref']);
  const actualEnd = begin + actualDataRows;

  const imageInfoMap = new Map();

  for (let rowIndex = begin + 1; rowIndex <= actualEnd; rowIndex++) {
    const imageCell = sheets['Basic Info'][`${productImageColumn}${rowIndex}`];
    if (!imageCell || !imageCell.v) continue;
    const imageUrl = imageCell.v.toString();

    try {
      const res =
        !imageMap || _isEmpty(imageMap)
          ? await uploadImage(imageUrl, token, customerId)
          : await uploadImageFromFile(
              imageUrl,
              imageMap[imageUrl],
              token,
              customerId
            );

      // Check for errors
      if (res.error === 'UploadError') {
        invalidImageUpload.push(rowIndex);
        continue;
      }

      // Check if got valid fileId and originalUrl
      if (!res.fileId || !res.originalUrl) {
        invalidImageUpload.push(rowIndex);
        continue;
      }

      // Check image dimension
      const dimension = await checkImageDimension(res.originalUrl);

      if (dimension.width !== 350 || dimension.height !== 350) {
        invalidImageDimension.push(rowIndex);
        continue;
      }

      imageInfoMap.set(rowIndex, {
        fileId: res.fileId,
        imageUrl: res.originalUrl,
      });
    } catch (error) {
      console.error('Fail to validate image', error);
      invalidImageUpload.push(rowIndex);
    }
  }

  return {
    isValid: invalidImageDimension.length + invalidImageUpload.length === 0,
    invalidImageDimension,
    invalidImageUpload,
    imageInfoMap,
  };
}

function s2ab(s) {
  if (typeof ArrayBuffer !== 'undefined') {
    const buf = new ArrayBuffer(s.length);
    const view = new Uint8Array(buf);
    for (let i = 0; i !== s.length; i++) view[i] = s.charCodeAt(i) & 0xff;
    return buf;
  } else {
    const buf = new Array(s.length);
    for (let i = 0; i !== s.length; i++) buf[i] = s.charCodeAt(i) & 0xff;
    return buf;
  }
}

export async function exportTemplate(
  isEdit = false,
  categories,
  type = 'product'
) {
  const h = getHeader(isEdit, type);

  const basicInfo = h.basicInfo.reduce((o, c) => {
    o[c.text] = null;
    return o;
  }, {});

  const categoryData = categories.map(cat => [cat.label]);
  const languageData = langUtils.getAppLangList().map(lang => [lang.label]);

  const workbook = [
    {name: 'Basic Info', data: [basicInfo]},
    {name: 'Category', data: categoryData},
    {name: 'Language', data: languageData},
  ];

  const wb = XLSX.utils.book_new();

  workbook.forEach((d, i) => {
    if (d) {
      const ws =
        i === 0
          ? XLSX.utils.json_to_sheet(d.data)
          : XLSX.utils.aoa_to_sheet(d.data);

      if (i === 0) {
        const head = h.basicInfo;
        head.forEach(col => {
          if (ws[`${col.column}1`]) {
            ws[`${col.column}1`].c = col.comments;
            ws[`${col.column}1`].c.hidden = true;
          }
        });
      }

      ws['!cols'] = [
        {wch: 15},
        {wch: 30},
        {wch: 15},
        {wch: 15},
        {wch: 15},
        {wch: 30},
      ];
      XLSX.utils.book_append_sheet(wb, ws, d.name);
    }
  });

  const wbout = XLSX.write(wb, {
    type: 'binary',
    bookType: 'xlsx',
  });

  const typeName = type === 'treatment' ? 'Treatment' : 'Product';
  return new Promise(resolve =>
    resolve(
      saveAs(
        new Blob([s2ab(wbout)], {type: 'application/octet-stream'}),
        isEdit
          ? `Edit Skincare ${typeName}_Skincare Pro.xlsx`
          : `Add Skincare ${typeName}_Skincare Pro.xlsx`
      )
    )
  );
}

export async function exportProducts(
  isEdit = true,
  categories,
  totalQuota,
  token,
  customerId,
  processedParams,
  type = 'product'
) {
  try {
    const tmp = new FormData();
    tmp.append('token', token);
    tmp.append('customerId', customerId);
    const type3 = type === 'treatment' ? 'SMB_TREATMENT' : 'SMB_PRODUCT';
    tmp.append(
      'types',
      JSON.stringify([{type1: 'Store', type2: 'SMB', type3}])
    );
    tmp.append('service', 'INVENTORY');

    const apiFormattedParams = _cloneDeep(processedParams);

    if (totalQuota !== Number.MAX_SAFE_INTEGER) {
      tmp.append('limit', totalQuota);
    }

    for (const [key, value] of Object.entries(apiFormattedParams)) {
      if (key === 'status' && value === 'Offline') {
        tmp.append(key, JSON.stringify(['Active', 'Offline']));
        continue;
      }
      if (
        key === 'createdTimeFrom' ||
        key === 'createdTimeTo' ||
        key === 'lastModifiedFrom' ||
        key === 'lastModifiedTo'
      ) {
        tmp.append(key, new Date(value).toISOString());
        continue;
      }
      tmp.append(key, value);
    }

    const res = await skincareProDao.getGenericList(tmp);
    let products = [];
    if (res.results) {
      products = res.results.map(item => {
        return {
          id: item.id,
          deletable: item.deletable,
          imageUrl: item.editingRevision.imageUrl,
          categoryId: item.categoryIds[0],
          status: item.editingRevision.status,
          createdTime: item.createdTime,
          lastModified: item.lastModified,
          name: item.editingRevision.name,
          i18n: item.editingRevision.i18n,
        };
      });
    }

    const header = getHeader(isEdit, type);

    if (!products || products.length === 0) {
      return exportTemplate(isEdit, categories, type);
    }

    let basicInfoSheet = [];
    let localizationSheet = [];

    products.forEach(product => {
      const normalizedProduct = normalizeProductData(product);

      // Basic Info Sheet
      const basic = {};
      header.basicInfo.forEach(col => {
        basic[col.text] =
          typeof col.accessor === 'function'
            ? col.key === 'category'
              ? col.accessor(categories, normalizedProduct)
              : col.accessor(normalizedProduct)
            : normalizedProduct[col.accessor];
      });
      basicInfoSheet.push(basic);

      // Product / Treatment Localization Sheet
      if (normalizedProduct.data && normalizedProduct.data.i18n) {
        Object.keys(normalizedProduct.data.i18n).forEach(lang => {
          const localization = {};
          header.localization.forEach(col => {
            localization[col.text] =
              typeof col.accessor === 'function'
                ? col.accessor(normalizedProduct, lang)
                : normalizedProduct[col.accessor];
          });
          localizationSheet.push(localization);
        });
      }
    });

    const categorySheet = categories.map(cat => [cat.label]);
    const languageSheet = langUtils.getAppLangList().map(lang => [lang.label]);

    const typeName = type === 'treatment' ? 'Treatment' : 'Product';

    const workbook = [
      {name: 'Basic Info', data: basicInfoSheet},
      {name: `${typeName} Localization`, data: localizationSheet},
      {name: 'Category', data: categorySheet},
      {name: 'Language', data: languageSheet},
    ];

    const wb = XLSX.utils.book_new();
    workbook.forEach((d, i) => {
      if (d) {
        const ws =
          i === 0 || i === 1
            ? XLSX.utils.json_to_sheet(d.data)
            : XLSX.utils.aoa_to_sheet(d.data);

        if (i === 0 || i === 1) {
          const head = i === 0 ? header.basicInfo : header.localization;
          head.forEach(col => {
            if (ws[`${col.column}1`]) {
              ws[`${col.column}1`].c = col.comments;
              ws[`${col.column}1`].c.hidden = true;
            }
          });
        }

        XLSX.utils.book_append_sheet(wb, ws, d.name);
      }
    });

    const wbout = XLSX.write(wb, {
      type: 'binary',
      bookType: 'xlsx',
    });

    return new Promise(resolve =>
      resolve(
        saveAs(
          new Blob([s2ab(wbout)], {type: 'application/octet-stream'}),
          isEdit
            ? `Edit Skincare ${typeName}_Skincare Pro.xlsx`
            : `Add Skincare ${typeName}_Skincare Pro.xlsx`
        )
      )
    );
  } catch (error) {
    return error;
  }
}

export async function uploadProducts(completeData, token, customerId, type) {
  const results = [];
  const type3 = type === 'treatment' ? 'SMB_TREATMENT' : 'SMB_PRODUCT';

  for (let i = 0; i < completeData.length; i++) {
    const product = completeData[i];
    const formData = new FormData();
    formData.append('token', token);
    formData.append('customerId', customerId);
    formData.append('type1', 'Store');
    formData.append('type2', 'SMB');
    formData.append('type3', type3);
    formData.append('categoryIds', product.categoryIds);
    formData.append('data', JSON.stringify(product.data));

    const result = await skincareProDao.createGenericList(formData);
    results.push(result);
  }

  return results;
}

export async function updateProducts(completeData, token, customerId, type) {
  const results = [];
  const type3 = type === 'treatment' ? 'SMB_TREATMENT' : 'SMB_PRODUCT';

  for (let i = 0; i < completeData.length; i++) {
    const product = completeData[i];
    const formData = new FormData();
    formData.append('token', token);
    formData.append('customerId', customerId);
    formData.append('type1', 'Store');
    formData.append('type2', 'SMB');
    formData.append('type3', type3);
    formData.append('categoryIds', product.categoryIds);
    formData.append('productId', product.productId);
    formData.append('data', JSON.stringify(product.data));

    const result = await skincareProDao.updateGenericList(formData);
    results.push(result);
  }

  return results;
}

function normalizeProductData(product) {
  const getI18nData = i18nData => {
    const i18nKeys = Object.keys(i18nData);
    const defaultKey = i18nKeys.find(key => i18nData[key].isDefault);
    return i18nData[defaultKey];
  };
  const i18nData = getI18nData(product.i18n);
  return {
    id: product.id,
    name: product.name,
    imageUrl: product.imageUrl,
    categoryIds: product.categoryId ? [product.categoryId] : [],
    actionUrl: i18nData.actionUrl,
    data: {
      ...product.data,
      i18n: product.i18n,
    },
  };
}
